

import { useState } from "react"

function ImageUploadForm({ onUpload, isUploading, currentUploadIndex, uploadProgress }) {
  // Images state
  const [imagesToUpload, setImagesToUpload] = useState([])
  const [pastedMarkingImage, setPastedMarkingImage] = useState(null)
  const [zoomedImage, setZoomedImage] = useState(null)

  // Handle paste event for question image
  const handlePaste = (event) => {
    const items = event.clipboardData.items
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile()
        const reader = new FileReader()
        reader.onload = (e) => {
          if (imagesToUpload.length < 20) {
            setImagesToUpload((prev) => [...prev, e.target.result])
          } else {
            alert("Maximum 20 images allowed. Please remove some images before adding more.")
          }
        }
        reader.readAsDataURL(file)
        break
      }
    }
  }

  // Handle file upload for question images
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      // Check if adding these files would exceed the limit
      if (imagesToUpload.length + files.length > 20) {
        alert("Maximum 20 images allowed. Please select fewer images.")
        return
      }

      // Process each file
      files.forEach((file) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          setImagesToUpload((prev) => [...prev, e.target.result])
        }
        reader.readAsDataURL(file)
      })
    }
  }

  // Remove an image from the upload list
  const removeImage = (index) => {
    setImagesToUpload((prev) => prev.filter((_, i) => i !== index))
  }

  // Handle file upload for marking scheme image
  const handleMarkingFileChange = (event) => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        setPastedMarkingImage(e.target.result)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()
    const success = await onUpload(imagesToUpload, pastedMarkingImage)
    if (success) {
      // Clear uploaded images after processing
      setImagesToUpload([])
    }
  }

  return (
    <div onPaste={handlePaste} className="mb-6 animate-fadeIn">
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow-lg rounded-xl px-6 py-5 mb-6 w-full mx-auto transition-all duration-300 border border-gray-100 backdrop-blur-sm bg-white/90 hover:shadow-xl transform hover:-translate-y-1"
      >
        <div className="mb-5">
          <h2 className="text-xl font-bold text-gray-800 mb-1 flex items-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 mr-2 text-blue-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            Upload Questions
          </h2>
          <p className="text-sm text-gray-500 ml-7">Upload or paste images of exam questions to begin grading</p>
        </div>

        {/* Flex container to align both upload fields horizontally */}
        <div className="flex flex-col md:flex-row gap-6 mb-5">
          {/* Question Image Upload */}
          <div className="flex-1">
            <label className="block text-gray-700 font-medium mb-2 text-sm">
              Question Images (Upload or Paste, max 20)
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="block w-full p-2.5 border border-gray-300 rounded-lg bg-white shadow-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-xs text-gray-500">
                {imagesToUpload.length}/20
              </div>
            </div>

            {/* Image Previews */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {imagesToUpload.map((img, idx) => (
                <div
                  key={idx}
                  className="relative group border border-gray-200 rounded-lg overflow-hidden shadow-sm transition-all duration-200 hover:shadow-md transform hover:scale-105"
                >
                  <img
                    src={img || "/placeholder.svg"}
                    alt={`Question ${idx + 1}`}
                    className="w-full h-20 object-cover cursor-pointer"
                    onClick={() => setZoomedImage(img)}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent text-white text-xs py-1 px-2">
                    Image {idx + 1}
                  </div>
                </div>
              ))}
            </div>

            {/* Image count indicator */}
            <div className="mt-2 text-xs text-gray-500 flex items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {imagesToUpload.length === 0
                ? "No images selected. Upload or paste images."
                : `${imagesToUpload.length} of 5 images selected`}
            </div>
          </div>

          {/* Marking Scheme Upload */}
          <div className="flex-1">
            <label className="block text-gray-700 font-medium mb-2 text-sm">Marking Scheme (Optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleMarkingFileChange}
              className="block w-full p-2.5 border border-gray-300 rounded-lg bg-white shadow-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />

            {/* Marking Scheme Preview with Click-to-Zoom */}
            {pastedMarkingImage ? (
              <div
                className="mt-3 relative group overflow-hidden border border-gray-200 rounded-lg shadow-sm transition-transform hover:shadow-md cursor-pointer transform hover:scale-105"
                onClick={() => setZoomedImage(pastedMarkingImage)}
              >
                <img
                  src={pastedMarkingImage || "/placeholder.svg"}
                  alt="Marking preview"
                  className="w-full h-auto max-h-40 object-contain rounded-lg"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-white text-sm font-semibold px-2 py-1 bg-black bg-opacity-50 rounded">
                    Click to Zoom
                  </span>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex items-center justify-center h-20 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                <div className="text-center text-gray-500 text-sm">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 mx-auto mb-1 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  No marking scheme uploaded
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Upload Button */}
        <button
          type="submit"
          disabled={isUploading || imagesToUpload.length === 0}
          className={`w-full py-3 ${
            isUploading
              ? "bg-blue-400 cursor-wait"
              : imagesToUpload.length === 0
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          } text-white font-semibold text-sm rounded-lg shadow-md focus:outline-none focus:ring-4 focus:ring-blue-300 transition-all duration-300 relative transform hover:scale-[1.02] active:scale-[0.98]`}
        >
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="flex items-center justify-center mb-1">
                <svg
                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Processing image {currentUploadIndex + 1} of {imagesToUpload.length}
              </div>

              {/* Enhanced Progress Bar */}
              <div className="w-full h-4 bg-blue-100 rounded-full overflow-hidden mt-2 mb-1 relative">
                <div
                  className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <span className="text-xs font-semibold text-white drop-shadow-md">{uploadProgress}%</span>
                </div>

                {/* Animated Pulse Effect */}
                <div
                  className="absolute top-0 left-0 h-full bg-white opacity-20 animate-pulse-progress"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>

              {/* Individual Image Progress Indicators */}
              <div className="w-full flex gap-1 mt-2">
                {Array.from({ length: imagesToUpload.length }).map((_, idx) => (
                  <div
                    key={idx}
                    className={`h-2 flex-1 rounded-full transition-all duration-500 relative overflow-hidden ${
                      idx < currentUploadIndex
                        ? "bg-blue-600"
                        : idx === currentUploadIndex
                          ? "bg-blue-400"
                          : "bg-blue-100"
                    }`}
                  >
                    {idx === currentUploadIndex && (
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer"></div>
                    )}
                  </div>
                ))}
              </div>

              <div className="text-xs text-center text-blue-700 mt-1 font-medium">
                {currentUploadIndex + 1} of {imagesToUpload.length}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload Questions
            </div>
          )}
        </button>

        {/* Zoom Modal */}
        {zoomedImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm transition-all duration-300"
            onClick={() => setZoomedImage(null)}
          >
            <div className="relative max-w-4xl w-full mx-4 animate-scaleIn">
              <button
                onClick={() => setZoomedImage(null)}
                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-lg z-10 hover:bg-red-100 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-gray-700"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={zoomedImage || "/placeholder.svg"}
                alt="Zoomed preview"
                className="max-w-full max-h-[90vh] rounded-lg shadow-2xl object-contain bg-white p-1"
              />
            </div>
          </div>
        )}
      </form>
    </div>
  )
}

export default ImageUploadForm
